import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";
import CustomImage from "../../atoms/customImage";
import { appendDots } from "../carouselHelpers";
import { PRODUCT_CARD_OPTIONS } from "../../../constants/common/splideOptions";
import VideoPlayer from "../../atoms/videoPlayer";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setCurrentVideoId, setLoadedVideoIds } from "../../../store/slices/plpSlice";
import { formatClassNames } from "../../../utils/common";
import { PLP_PRODUCT_VIDEO_POSITION } from "../../../constants/pageConstants/plpConstants";
import { useCardHoverImages } from "./useCardHoverImages";

/**
 * A slider component that displays a list of product images.
 * It supports autoplay functionality, which can be controlled by the parent component.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {string[]} props.productImgList - An array of image URLs to display in the slider.
 * @param {number} props.imgWidth - The width of each image in the slider.
 * @param {number} props.imgHeight - The height of each image in the slider.
 * @param {string} props.alt - The alt text for each image in the slider.
 * @param {string} props.defaultImg - The default image URL to display when `productImgList` is empty.
 * @param {boolean} props.isPriority - Whether the images should be prioritized for loading.
 * @param {boolean} props.isMounted - check client or server rendering.
 * @param {React.Ref} ref - A ref object that allows the parent component to control the slider (e.g., start or stop autoplay).
 *
 * Only the first image is requested on landing. Later images are requested in pairs: the next two, then the next two.
 * The slider advances only after the destination image has loaded.
 *
 * @returns {JSX.Element} The rendered slider component.
 */
const ProductCardImageSlider = forwardRef(
  (
    {
      productImgList = [],
      imgWidth,
      imgHeight,
      alt,
      defaultImg,
      isPriority = false,
      isMounted,
      productId,
      isPLP = false,
      isSearch = false,
      videoUrl = "",
      isPlayVideoOnCardHover = false,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const { loadedVideoIds, currentVideoId } = useSelector(
      (state) => ({
        loadedVideoIds: state?.plp?.loadedVideoIds,
        currentVideoId: state?.plp.currentVideoId,
      }),
      shallowEqual,
    );

    const sliderRef = useRef(null);
    const videoElementRef = useRef(null);
    const currentVideoIdRef = useRef(currentVideoId);
    const loadedVideoIdsRef = useRef(loadedVideoIds);
    const productImgListRef = useRef(productImgList);
    const playMountedVideoRef = useRef(() => {});

    productImgListRef.current = productImgList;
    const {
      mountedThroughIndex,
      areAllSlidesReady,
      onSlideImageReady,
      requestHoverImages,
      holdForVideo,
      continueAfterVideo,
      cancelHoverImages,
      holdForVideoRef,
    } = useCardHoverImages({ sliderRef, productImgListRef });

    useEffect(() => {
      if (!videoUrl || !holdForVideoRef.current || !sliderRef.current) {
        return;
      }
      const frame = window.requestAnimationFrame(() => {
        sliderRef.current?.slickGoTo(PLP_PRODUCT_VIDEO_POSITION, true);
        sliderRef.current?.slickPause();
      });
      return () => window.cancelAnimationFrame(frame);
    }, [mountedThroughIndex, areAllSlidesReady, videoUrl]);

    useEffect(() => {
      if (currentVideoId) {
        currentVideoIdRef.current = currentVideoId;
      }
    }, [currentVideoId]);

    useEffect(() => {
      if (loadedVideoIds) {
        loadedVideoIdsRef.current = loadedVideoIds;
      }
    }, [loadedVideoIds]);

    /**
     * Returns the video element id.
     * @function
     * @returns {string} The video element id.
     */
    const getVideoElementId = () => `${productId}-${PLP_PRODUCT_VIDEO_POSITION}`;

    /**
     * Resolves the active video element from ref, slick slide, or document id.
     *
     * @param {string} videoId
     * @returns {HTMLVideoElement|null}
     */
    const resolveVideoElement = (videoId) => {
      if (videoElementRef.current) {
        return videoElementRef.current;
      }

      const activeVideo = sliderRef.current?.innerSlider?.list?.querySelector?.(".slick-active video");
      return activeVideo || document.getElementById(videoId);
    };

    /**
     * Pauses the slider and resets it to the first slide after a short delay.
     * @function
     */
    const pauseAndResetSlide = () => {
      if (!sliderRef.current) {
        return;
      }
      sliderRef.current.slickPause();
      setTimeout(() => {
        sliderRef.current?.slickGoTo(0, true);
      }, 400);
    };

    const hasMultipleImages = productImgList.length > 1;
    const canLoopSlides = mountedThroughIndex >= productImgList.length - 1 && areAllSlidesReady && hasMultipleImages;
    const isInfinite = canLoopSlides && !(videoUrl && isPlayVideoOnCardHover) && PRODUCT_CARD_OPTIONS.infinite;

    const settings = {
      ...PRODUCT_CARD_OPTIONS,
      infinite: isInfinite,
      dots: hasMultipleImages,
      autoplay: false,
      pauseOnHover: false,
      appendDots: (dots) => appendDots(dots, "", "", "", "", "productCardDots"),
    };

    /**
     * Handles mouse leave event on the video player. Pauses the video.
     * @function
     * @param {string} videoId - The DOM id of the video element to pause.
     */
    const onHandleMouseLeave = (videoId) => {
      resolveVideoElement(videoId)?.pause();
    };

    /**
     * Pauses a finished video and moves to the next image immediately once that image has loaded.
     * @function
     * @param {string} videoId - The DOM id of the video element that ended.
     */
    const onHandleVideoEnd = (videoId) => {
      const video = resolveVideoElement(videoId);
      if (!video) {
        return;
      }
      video.pause();
      continueAfterVideo();
    };

    /**
     * Checks if the entire video is buffered/loaded.
     *
     * @param {HTMLVideoElement} video
     * @param {string} videoId
     * @returns {boolean}
     */
    const checkIfFullyLoaded = (video, videoId) => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const duration = video.duration;
        if (duration > 0 && bufferedEnd >= duration) {
          dispatch(setLoadedVideoIds({ [videoId]: true }));
          return true;
        }
      }
      return false;
    };

    /**
     * Checks if the video is fully loaded on progress events.
     *
     * @param {HTMLVideoElement} video
     * @param {string} videoId
     */
    const onProgress = (video, videoId) => {
      if (checkIfFullyLoaded(video, videoId)) {
        video.removeEventListener("progress", () => onProgress(video, videoId));
      }
    };

    /**
     * Loads and plays the product video.
     *
     * @param {string} videoId
     */
    const onHandleMouseOver = (videoId) => {
      if (sliderRef.current) {
        sliderRef.current.slickPause();
      }

      if (currentVideoIdRef.current !== videoId) {
        const prevVideo = resolveVideoElement(currentVideoIdRef.current);
        if (prevVideo) {
          prevVideo.pause();
          if (!loadedVideoIdsRef.current[currentVideoIdRef.current]) {
            prevVideo.removeAttribute("src");
            prevVideo.load();
          }
        }
        dispatch(setCurrentVideoId(videoId));
      }

      const video = resolveVideoElement(videoId);
      if (!video) {
        return;
      }

      if (!loadedVideoIdsRef.current[videoId]) {
        const source = video.dataset.src || video.getAttribute("data-src");
        if (source && !video.getAttribute("src")) {
          video.setAttribute("src", source);
          video.load();
        }
      }

      if (!checkIfFullyLoaded(video, videoId)) {
        video.addEventListener("progress", () => onProgress(video, videoId));
      }

      video.muted = true;
      video.play()?.catch(() => {});
    };

    /**
     * Plays the already mounted product video. The video file stays deferred until this runs.
     */
    playMountedVideoRef.current = () => {
      if (!sliderRef.current) {
        return;
      }
      sliderRef.current.slickGoTo(PLP_PRODUCT_VIDEO_POSITION, true);
      sliderRef.current.slickPause();
      window.setTimeout(() => onHandleMouseOver(getVideoElementId()), 100);
    };

    useImperativeHandle(
      ref,
      () => ({
        /**
         * Loads the remaining images, then starts slider autoplay.
         * @function
         */
        startAutoplay() {
          requestHoverImages(true);
        },
        /**
         * Stops the autoplay of the slider.
         * @function
         */
        stopAutoplay() {
          cancelHoverImages();
          pauseAndResetSlide();
        },
        /**
         * Plays the mounted video immediately and requests the next image pair while it plays.
         * @function
         */
        playVideo() {
          if (!videoUrl || !productId) {
            return;
          }
          holdForVideo();
          requestHoverImages(false, true);
          playMountedVideoRef.current();
        },
        /**
         * Pauses the video.
         * @function
         */
        pauseVideo() {
          if (!videoUrl || !productId) {
            return;
          }
          cancelHoverImages();
          onHandleMouseLeave(getVideoElementId());
          pauseAndResetSlide();
        },
      }),
      [dispatch, productId, videoUrl],
    );

    return (
      <>
        {!isMounted && productImgList.length >= 0 ? (
          <CustomImage
            src={defaultImg}
            width={imgWidth}
            height={imgHeight}
            alt={alt}
            isPriority={isPriority}
            customStyle={{ width: "100%", height: "100%" }}
            itemProp="image"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        ) : (
          <Slider
            {...settings}
            ref={sliderRef}
            className="respective w-full"
            dotsClass={formatClassNames("slick-dots", (isPLP || isSearch) && videoUrl && "plpPlayDots")}
          >
            {productImgList.map((item, index) => (
              <div key={videoUrl && index === PLP_PRODUCT_VIDEO_POSITION ? `video-${productId}` : `img-${index}`}>
                {videoUrl && index === PLP_PRODUCT_VIDEO_POSITION ? (
                  <VideoPlayer
                    videoUrl={videoUrl}
                    preload="none"
                    customRef={videoElementRef}
                    onMouseEnter={() => onHandleMouseOver(`${productId}-${index}`)}
                    onMouseLeave={() => onHandleMouseLeave(`${productId}-${index}`)}
                    onEnded={() => onHandleVideoEnd(`${productId}-${index}`)}
                    aria-label="Video player"
                    id={`${productId}-${index}`}
                    isMuted={true}
                    poster={productImgList[index + 1]}
                    width={imgWidth}
                    height={imgHeight}
                  />
                ) : index <= mountedThroughIndex ? (
                  <CustomImage
                    src={item}
                    width={imgWidth}
                    height={imgHeight}
                    alt={alt}
                    isPriority={isPriority && index === 0}
                    {...(index === 0 ? {} : { loading: "eager" })}
                    imageRef={(node) => {
                      if (node?.complete && node.naturalWidth > 0) onSlideImageReady(index);
                    }}
                    onLoad={() => onSlideImageReady(index)}
                    onError={() => onSlideImageReady(index)}
                    customStyle={{ width: "100%", height: "100%" }}
                    itemProp="image"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                ) : null}
              </div>
            ))}
          </Slider>
        )}
      </>
    );
  },
);

ProductCardImageSlider.displayName = "ProductCardImageSlider";

ProductCardImageSlider.propTypes = {
  productImgList: PropTypes.arrayOf(PropTypes.string),
  imgWidth: PropTypes.number.isRequired,
  imgHeight: PropTypes.number.isRequired,
  alt: PropTypes.string.isRequired,
  defaultImg: PropTypes.string,
  isPriority: PropTypes.bool,
  isMounted: PropTypes.bool,
  isPLP: PropTypes.bool,
  isSearch: PropTypes.bool,
  productId: PropTypes.string,
  videoUrl: PropTypes.string,
  isPlayVideoOnCardHover: PropTypes.bool,
};

export default ProductCardImageSlider;
